/**
 * Healthcare-contextualized neuroscience derivations.
 *
 * Reuses all computation functions from brainHealth neuroscience engine.
 * Adds healthcare-specific temporal outcome labels and descriptions.
 *
 * The neurobiological pathway (8 pathways → CREB → BDNF → outcomes) is
 * identical regardless of building type — environments affect brains
 * the same way. What differs is the clinical framing of outcomes.
 */

// Re-export all core computation functions unchanged
export {
  computeActivating,
  computeInhibiting,
  computeSynergy,
  computeBdnfBreakdown,
  computePathwayContributions,
  classifyNetEffect,
  netEffectDescription,
} from '../../brainHealth/engine/neuroscience';

function clamp01(v) {
  return Math.max(0, Math.min(1, v));
}

/**
 * Compute temporal outcomes with healthcare-contextualized labels.
 * Same math as brainHealth version — different clinical framing.
 */
export function computeTemporalOutcomesHealthcare(pathwayScores) {
  const mov = pathwayScores.movement ?? 0;
  const day = pathwayScores.daylight ?? 0;
  const nat = pathwayScores.nature ?? 0;
  const enr = pathwayScores.enrichment ?? 0;
  const air = pathwayScores.air_quality ?? 0;
  const noise = pathwayScores.noise ?? 0;

  const immediate = clamp01(0.40 * mov + 0.30 * day + 0.15 * enr + 0.15 * (1 + noise));
  const shortTerm = clamp01(0.30 * nat + 0.25 * (1 + air) + 0.25 * (1 + noise) + 0.20 * enr);
  const longTerm = clamp01(0.35 * enr + 0.30 * mov + 0.20 * nat + 0.15 * day);

  return {
    immediate: {
      score: Math.round(immediate * 100),
      label: 'Immediate (hours)',
      items: [
        'Acute stress reduction for patients and staff',
        'Pain modulation via nature views and daylight',
        'Circadian entrainment supporting sleep onset',
      ],
    },
    shortTerm: {
      score: Math.round(shortTerm * 100),
      label: 'Short-term (days-weeks)',
      items: [
        'Wound healing acceleration',
        'Immune function support (reduced HAI susceptibility)',
        'Sleep quality improvement',
        'Delirium risk reduction (Choi 2012: 36% reduction with daylight)',
      ],
    },
    longTerm: {
      score: Math.round(longTerm * 100),
      label: 'Long-term (weeks-months)',
      items: [
        'Reduced hospital-acquired cognitive decline',
        'Sustained analgesic/opioid reduction',
        'Post-discharge outcome improvement',
        'Staff neuroplasticity preservation (reduced burnout-related cognitive decline)',
      ],
    },
  };
}

/**
 * Healthcare-specific pathway mechanism descriptions for tooltips.
 */
export const HC_PATHWAY_MECHANISMS = {
  daylight: {
    label: 'Daylight',
    mechanism: 'Melanopic light → ipRGC activation → SCN circadian entrainment → N-acetylserotonin → TrkB/BDNF. In hospitals: supports patient circadian rhythm, accelerates post-operative recovery, reduces delirium.',
  },
  nature: {
    label: 'Nature',
    mechanism: 'Nature exposure → cortisol reduction (9.6%/hr) → reduced CREB inhibition → BDNF preservation. Ulrich 1984: nature view patients discharged 0.74 days sooner with 22% less analgesic.',
  },
  movement: {
    label: 'Movement',
    mechanism: 'Physical activity → PGC-1α/FNDC5/irisin + lactate/SIRT1 → BDNF. For patients: early ambulation reduces decompensation. For staff: reduces fatigue from excessive walking via better unit layout.',
  },
  enrichment: {
    label: 'Enrichment',
    mechanism: 'Cognitive stimulation → delayed BDNF pathway (Nicastri 2022). In hospitals: reduces patient boredom and cognitive decline during stays; supports staff cognitive engagement.',
  },
  wayfinding: {
    label: 'Wayfinding',
    mechanism: 'Spatial navigation → hippocampal engagement → neuroplasticity. In hospitals: reduces patient/visitor anxiety and staff time lost to giving directions; critical for emergency response times.',
  },
  noise: {
    label: 'Noise (inhibiting)',
    mechanism: 'High-intensity noise → hippocampal BDNF depletion (26% at 110 dB) + GABAergic potentiation. Hospital ICUs average 72 dB. Disrupts patient sleep → delays recovery; contributes to staff burnout.',
  },
  air_quality: {
    label: 'Air Quality (inhibiting)',
    mechanism: 'PM2.5 → PKA/CREB/BDNF suppression. In hospitals: poor ventilation increases HAI risk; adequate ACH rates and HEPA filtration are primary infection control mechanisms.',
  },
  visual_stress: {
    label: 'Visual Complexity (inhibiting)',
    mechanism: 'Repetitive patterns (~3 cpd) → cortical hyperactivation → CREB inhibition. Hospital fluorescent lighting and tiled corridors often create high visual stress, contributing to patient discomfort and staff fatigue.',
  },
};
